import { useState } from 'react';
import { useContext } from 'react';
import time from 'utils/time';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { createFiltersProperties } from 'components/Dashboard/helpers/controlActiveFilters';
import CrmContactsSubNav, {
  CrmGroupsSelectedSubNav,
} from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/CrmContactsSubNav';
import AddContactButton from 'components/Dashboard/Influence/CrmContacts/AddContactButton';
import AddContactModal from 'components/Dashboard/Influence/CrmContacts/AddContactModal';
import ClearFiltersButton from 'components/Dashboard/SubNav/ui/ClearFiltersButton';
import useDetectActiveFilters from 'components/Dashboard/SubNav/useDetectActiveFilters';
import InfluenceNotIncludedFeaturesPopup from 'components/Dashboard/Influence/CrmContacts/InfluenceNotIncludedFeaturesPopup';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AIAssistantCreateButtons } from 'components/WritingAssistant/ui/MyContent';
import { CreateGroupButton } from 'components/Dashboard/Influence/CrmGroups/CreateGroupButton';
import InfluenceSectionTitle from 'components/Dashboard/Influence/InfluenceSectionTitle/InfluenceSectionTitle';
import MainSearchBar from 'components/Dashboard/MainSearchBar';
import EngagementStatisticsDropdown from 'components/Dashboard/Influence/EngagementStatisticsDropdown';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import MoreFiltersButton from 'components/Dashboard/Filters/ui/MoreFiltersButton';
import GroupViewSubnav from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/GroupViewSubnav';
import EmailPerformanceNav from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/EmailPerformanceNav';
import CreateEmailLink from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/CreateEmailLink';
import SubnavSearchBar from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/SubnavSearchBar';
import EmailComponentNav from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/EmailComponentNav';
import StakeholdersSubNav from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/StakeholdersSubNav';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import getAppSite from 'utils/getAppSite';
import LogInteractionModal from 'components/Dashboard/Influence/CrmInteractions/LogInteractionModal/LogInteractionModal';
import { useInteractionsList } from 'components/Dashboard/Influence/CrmInteractions/InteractionsListContext';
import getInteractionDisplayType from 'components/Dashboard/Influence/CrmInteractions/getInteractionDisplayType';
import EngagementStatsSubNavActions from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/EngagementStatsSubNavActions';
import EmailsSectionTabs from 'components/Dashboard/Influence/InfluenceCrmSubNav/components/EmailsSectionTabs';
import useEmailDomainFunctions from 'components/Dashboard/hooks/useEmailDomainFunctions';

const InfluenceCrmSubNav = () => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { readyKeywordLists, selectedCrmContacts, showAddContactModal, activePlan, selectedGroups, interactionDetail } =
    state;
  const { showLogInteractionModal, setShowLogInteractionModal } = useInteractionsList();

  const [isStakeholdersSelected, setIsStakeholdersSelected] = useState(false);

  const location = useLocation();
  const params = useParams();
  const { crmEmailId, groupId } = params;

  const isMobile = useIsMobile(992);
  const isSmallMobile = useIsMobile(480);

  const { enableCrm } = activePlan;
  const filtersProperties = createFiltersProperties();
  const innerPath = location.pathname.split('/')[2];
  const isInteractionDetail = innerPath === 'interactions' && !!location.pathname.split('/')[3];
  const emailsListPath = location.pathname === '/people/emails';
  const emailsSectionPath = emailsListPath || (innerPath === 'email-performance' && !crmEmailId);
  // Send emails / Email performance only make sense as top-level tabs before a team can actually send
  // (nothing to switch between once set up) - once authenticated it reverts to a plain page title, and
  // email performance is reached per-campaign instead (see EmailsSectionTabs.jsx's own callers).
  const { needsDomainVerification } = useEmailDomainFunctions();
  const showEmailsTabs = emailsSectionPath && needsDomainVerification;

  const { selectedIds, selectedAll, selectedAllVisible } = selectedCrmContacts;
  const isContactsSelected = selectedIds.length > 0 || selectedAll || selectedAllVisible.length > 0;
  const contactsBarCondition =
    (location.pathname.includes('/directory') ||
      location.pathname.includes('/my-groups') ||
      location.pathname === '/people/stakeholder-mapping') &&
    isContactsSelected;

  const groupsCondition = selectedGroups?.selectedAll || selectedGroups?.selectedIds?.length > 0;
  const activeSubNavCondition =
    groupsCondition || contactsBarCondition || (isStakeholdersSelected && innerPath?.includes('stakeholder'));

  const showHideAddContactModal = () => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'showAddContactModal',
      value: !showAddContactModal,
    });
  };
  const getSubNavTitle = () => {
    if (location.pathname.includes('/policymakers')) {
      return `Policymakers ${isMobile ? '' : 'by topic'}`;
    } else {
      return 'People';
    }
  };

  const searchBarSubNavCondition =
    innerPath === 'directory' ||
    (innerPath === 'emails' && !crmEmailId && !location.pathname.includes('emails/create')) ||
    innerPath === 'my-lobbying-material' ||
    (innerPath === 'interactions' && !isInteractionDetail) ||
    (innerPath === 'my-groups' && !groupId && !groupsCondition);

  return (
    <>
      <InfluenceNotIncludedFeaturesPopup
        showPopup={showAddContactModal && !enableCrm}
        type={'contacts'}
        setShowPopup={showHideAddContactModal}
      />
      {enableCrm && (
        <AddContactModal
          showHideAddContactModal={showHideAddContactModal}
          showAddContactModal={showAddContactModal}
          fromContactsSelect
        />
      )}
      {showLogInteractionModal && (
        <LogInteractionModal
          onClose={() => setShowLogInteractionModal(false)}
          onSuccess={() => {
            setShowLogInteractionModal(false);
            dispatch({ type: 'MODIFY_SECTION', parameter: 'interactionLogged', value: true });
          }}
        />
      )}
      <div
        className={`contact-crm-subnav inbox-items-bar monitor-items-bar trends-subnav-bar crm-contacts-subnav-bar ${
          activeSubNavCondition ? 'subnav-contacts-selected' : ''
        }`}
      >
        <div className='mx-auto main-content-wrapper flex-centered justify-content-between'>
          {innerPath === 'emails' && (crmEmailId || location.pathname.split('/')[3] === 'create') ? (
            <EmailComponentNav />
          ) : (
            <>
              <div className='side-left-element pl-md-5 pl-3 d-none d-lg-block'>
                <p className='mb-0 title-h4 nowrap-item influence-subnav-tab-title'>
                  <>
                    {contactsBarCondition || groupsCondition ? (
                      ''
                    ) : (
                      <>
                        {groupId ? (
                          <Link
                            className='general-button action-button rounded-pill px-4 paragraph-p1 paragraph-p2 d-inline-block'
                            to={location?.state?.returnUrl ?? `/people/my-groups`}
                          >
                            <span className='icon-long-arrow-left mr-2 paragraph-p4' /> Back to{' '}
                            {location?.state?.returnUrl?.includes('directory') ? 'directory' : 'groups'}
                          </Link>
                        ) : isInteractionDetail ? (
                          <Link
                            className='general-button action-button rounded-pill px-4 paragraph-p1 paragraph-p2 d-inline-block'
                            to={location?.state?.returnUrl ?? '/people/interactions'}
                          >
                            <span className='icon-long-arrow-left mr-2 paragraph-p4' /> Back to interactions
                          </Link>
                        ) : (
                          <>{getSubNavTitle()}</>
                        )}
                      </>
                    )}
                  </>
                </p>
              </div>

              <div
                className={`main-content flex-centered  filters-general-container justify-content-between pr-xl-5 ${
                  groupId && !contactsBarCondition ? '' : 'px-md-4 px-3'
                }`}
              >
                {isInteractionDetail && (
                  <div className='d-flex align-items-center flex-grow-1' style={{ minWidth: 0 }}>
                    <span className='icon-chat title-h3 mr-3 flex-shrink-0' />
                    <p className='mb-0 title-h4 nowrap-item hidden-lines hidden-one-line d-flex align-items-center'>
                      <span>{getInteractionDisplayType(interactionDetail ?? {})}</span>
                      {interactionDetail?.dateTime && (
                        <>
                          <span className='dot specific-right-margin-6 d-inline-block specific-top-margin-3'></span>
                          <span>{time(interactionDetail.dateTime).format('DD MMM[,] YYYY')}</span>
                        </>
                      )}
                    </p>
                  </div>
                )}
                {isInteractionDetail && interactionDetail?.sourceCrmEmailId && (
                  // Mobile version moved to its own full-width row below the subnav (see the
                  // isMobile block further down) - competing with the title for the same row here
                  // was what overlapped them; desktop has the room to keep it inline.
                  <a
                    className='general-button action-button rounded-button px-4 paragraph-p2 nowrap-item d-none d-lg-inline-block'
                    href={`/people/email-performance/${interactionDetail.sourceCrmEmailId}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    See email performance
                  </a>
                )}
                {searchBarSubNavCondition && (
                  <>
                    <RenderSearchNav contactsBarCondition={contactsBarCondition} />
                  </>
                )}

                {innerPath === 'engagement-stats' && <InfluenceSectionTitle />}
                {innerPath === 'engagement-stats' && <EngagementStatsSubNavActions />}

                {(innerPath?.includes('stakeholder') || location.pathname.includes('/policymakers')) &&
                  !contactsBarCondition && (
                    <>
                      <StakeholdersSubNav
                        isMobile={isMobile}
                        isSmallMobile={isSmallMobile}
                        readyKeywordLists={readyKeywordLists}
                        filtersProperties={filtersProperties}
                        setIsStakeholdersSelected={setIsStakeholdersSelected}
                      />
                    </>
                  )}

                <div className={`crm-contacts-select-subnav ${!contactsBarCondition ? 'd-none' : ''}`}>
                  <CrmContactsSubNav isSmallMobile={isSmallMobile} />
                </div>

                <div className={`flex-centered justify-content-lg-between w-100 ${!groupsCondition ? 'd-none' : ''}`}>
                  <CrmGroupsSelectedSubNav />
                </div>

                {innerPath?.includes('email-performance') && <EmailPerformanceNav />}
                {innerPath?.includes('ai-assistant') && (
                  <div className='influence-ai-assistant-container'>
                    <AIAssistantCreateButtons influencePage={true} />
                  </div>
                )}
                {groupId && !contactsBarCondition && <GroupViewSubnav />}
              </div>
            </>
          )}
        </div>
      </div>
      {isMobile && (
        <>
          {(location.pathname.includes('/stakeholder-mapping') ||
            location.pathname.includes('/policymakers') ||
            searchBarSubNavCondition ||
            innerPath?.includes('engagement-statistics') ||
            showEmailsTabs ||
            (isInteractionDetail && interactionDetail?.sourceCrmEmailId)) && (
            <div className='w-100 px-3 bg-main-white border-bottom pb-3'>
              <div className={`w-100 pt-3 d-block`}>
                {showEmailsTabs && (
                  <div className='mb-3'>
                    <EmailsSectionTabs />
                  </div>
                )}
                {isInteractionDetail && interactionDetail?.sourceCrmEmailId && (
                  <a
                    className='general-button action-button rounded-button px-4 paragraph-p2 nowrap-item d-block w-100 text-center'
                    href={`/people/email-performance/${interactionDetail.sourceCrmEmailId}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    See email performance
                  </a>
                )}
                {(location.pathname.includes('/stakeholder-mapping') ||
                  location.pathname.includes('/policymakers')) && <MainSearchBar />}
                {searchBarSubNavCondition && (
                  <SubnavSearchBar
                    placeholder={innerPath === 'interactions' ? 'Search by name, type, description or tag' : undefined}
                  />
                )}
                {innerPath?.includes('engagement-statistics') && <EngagementStatisticsDropdown mobileView />}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

//SUBNAV FOR CONTACTS AND EMAILS
const RenderSearchNav = (props) => {
  const { contentResults } = useContentResults();

  const { CrmContacts } = contentResults ?? {};

  const { detectActiveFilters } = useDetectActiveFilters();

  const { contactsBarCondition } = props;
  const isMobile = useIsMobile();
  const location = useLocation();
  const directoryPath = location.pathname.includes('/directory');
  const interactionsPath = location.pathname.includes('/interactions');
  const emailsListPath = location.pathname === '/people/emails';
  const { needsDomainVerification } = useEmailDomainFunctions();

  const resultsBadge = directoryPath &&
    CrmContacts?.readyCrmContactsFilters &&
    (detectActiveFilters() || getUrlParam('search')) && (
      <span className='paragraph-p1 ml-1 sent-state-color border rounded-pill specific-x-padding-18 specific-top-padding-6 specific-bottom-padding-7 mr-3 nowrap-item'>
        {thousandCommas(CrmContacts?.totalHits) ?? 0} result
        {CrmContacts?.totalHits === 1 ? '' : 's'}
      </span>
    );

  return (
    <>
      {!contactsBarCondition && (
        <>
          <div className={`w-100 flex-centered`}>
            {interactionsPath && !isMobile ? (
              <>
                {/* mirrors the 250px filters sidebar width below, so edges line up */}
                <div className='flex-grow-1 flex-centered justify-content-between'>
                  <InfluenceSectionTitle />
                  {resultsBadge}
                  <SubnavSearchBar placeholder='Search by name, type, description or tag' className='mr-0' />
                </div>
                <div className='ml-4 flex-centered justify-content-end' style={{ width: '250px' }}>
                  <IncludeStaffButton />
                  <RenderActionButton />
                </div>
              </>
            ) : (
              <>
                <div className='flex-grow-1 flex-centered'>
                  {emailsListPath && !isMobile && needsDomainVerification && <EmailsSectionTabs />}
                  <InfluenceSectionTitle />
                </div>
                <div className={`flex-centered `}>
                  {!isMobile ? (
                    <>
                      {resultsBadge}
                      <SubnavSearchBar />
                    </>
                  ) : (
                    <>
                      {directoryPath && (
                        <>
                          <div className='mr-2'>
                            <MoreFiltersButton section={'InfluenceContacts'} />
                          </div>
                        </>
                      )}
                      {interactionsPath && (
                        <div className='mr-2'>
                          <MoreFiltersButton section={'CrmInteractions'} />
                        </div>
                      )}
                    </>
                  )}
                </div>
                {!isMobile && <IncludeStaffButton />}
                <RenderActionButton />
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

const RenderActionButton = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { listDomainsVerified } = state;

  const location = useLocation();
  const params = useParams();
  const { groupId } = params;

  const { detectActiveFilters } = useDetectActiveFilters();
  const activeFilters = detectActiveFilters();

  if (location.pathname.includes('/directory')) {
    return (
      <>
        <AddContactButton />
      </>
    );
  } else if (location.pathname.includes('/emails')) {
    if (!!listDomainsVerified.verifiedDomains.length || !!listDomainsVerified.verifiedEmailAddresses.length) {
      return (
        <CreateEmailLink
          showToolTip={
            !listDomainsVerified.verifiedDomains.length && !listDomainsVerified.verifiedEmailAddresses.length
          }
        />
      );
    } else {
      return (
        <a
          href='http://support.policymogul.com/en/articles/7936585-sending-emails-from-policymogul'
          target='_blank'
          rel='noopener noreferrer'
        >
          <button className='setup-guide-emails-btn'>Setup guide</button>
        </a>
      );
    }
  } else if (location.pathname.includes('/interactions') && !location.pathname.split('/')[3]) {
    return <LogInteractionButtonSubNav />;
  } else if (location.pathname.includes('/my-lobbying-material')) {
    return <button className='influence-team-content-fake-btn' />;
  } else if (location.pathname.includes('/my-groups') && !groupId) {
    return (
      <>
        {activeFilters && <ClearFiltersButton contactsSubNav />}
        <CreateGroupButton />
      </>
    );
  }
};

const IncludeStaffButton = (props) => {
  const { addMarginBottom, title } = props;
  const includeStaff = getUrlParam('includeStaff') === 'true';
  const location = useLocation();
  const navigate = useNavigate();
  const directoryPath = location.pathname.includes('/directory');

  const { getDefaultSectionFilters, filtersState, applyFiltersFunction, filterTypeModifiers } = useFilters();
  const sectionFilters = getDefaultSectionFilters(filtersState);

  const activeStaff = () => {
    const roleFilter = sectionFilters?.find((item) => item?.name === 'Role');
    const staffOption = roleFilter?.options?.filter(
      (item) => item?.name?.toLowerCase()?.includes('staff') && item?.selected
    );
    return staffOption?.length > 0 || includeStaff;
  };

  const applyFilter = ({ value }) => {
    const filter = sectionFilters?.find((item) => item?.name === 'Role');
    const searchParams = new URLSearchParams(location?.search);
    searchParams.set('includeStaff', value);
    navigate({ search: searchParams.toString(), hash: location.hash }, { replace: true });

    if (filter?.options?.some((item) => item?.selected)) {
      let newValue = [...filter?.options];
      newValue = newValue.map((item) => {
        if (item?.name?.toLowerCase()?.includes('staff')) {
          return { ...item, selected: value };
        } else {
          return item;
        }
      });
      let newFilters = filterTypeModifiers({ filter, newValue });
      applyFiltersFunction(newFilters);
    }
  };

  return (
    <>
      {title && <h3>{title}</h3>}
      {directoryPath && (
        <button
          className={`general-button main-light-text border specific-y-padding-7 specific-x-padding-22 mr-3 rounded-button d-flex nowrap-item ${
            addMarginBottom ? 'mb-3' : ''
          }`}
          onClick={(e) => {
            applyFilter({ value: !activeStaff() });
          }}
        >
          <span>Include staff </span>
          <span
            className='icon-info specific-top-margin-6 specific-left-margin-8 paragraph-p3'
            data-tooltip-id='general-tooltip'
            data-tooltip-content={`If you include staff, certain filters that apply to policymakers only (e.g. 'Vocal about...' ${
              getAppSite() === 'global' ? ` and 'Region'` : ''
            }) will show results for both policymakers and their staff`}
            data-tooltip-class-name='billing-tooltip'
            data-tooltip-place='bottom'
          />
          <span className={`specific-left-margin-18 toggle-button-${activeStaff() ? 'active' : 'inactive'}`}>
            <span className='toggle-button' />
          </span>
        </button>
      )}
    </>
  );
};

const LogInteractionButtonSubNav = () => {
  const { setShowLogInteractionModal } = useInteractionsList();
  const isSmallMobile = useIsMobile(480);
  const openLogInteractionModal = () => setShowLogInteractionModal(true);

  return isSmallMobile ? (
    <button className='add-to-contact-mobile-button' onClick={openLogInteractionModal} />
  ) : (
    <button
      // Matches AddContactButton's own classes exactly - same "primary CTA" pill used for Create
      // new contact / Create email campaign, so it doesn't read as a smaller, less important action.
      className='general-button sort-button d-lg-block top-nav-create-contact-button nowrap-item'
      onClick={openLogInteractionModal}
    >
      Log an interaction
    </button>
  );
};

export { IncludeStaffButton };
export default InfluenceCrmSubNav;
